import {Component, EventEmitter, HostListener, Inject, OnDestroy, ViewChild} from '@angular/core';
import {CONTACTS_DATA_SERVICE, ContactsDataService} from '../../../../services/contacts/contacts-data.service';
import {LEADS_DATA_SERVICE, LeadsDataService} from '../../../../services/leads/leads-data.service';
import {Router} from '@angular/router';
import {UserTokenService} from '../../../../services/user-service/user-token.service';
import {Select, Store} from '@ngxs/store';
import {SortService} from '../../../../services/sort/sort.service';
import {
  LeadDataObject,
  LeadsData,
  LeadsQueryRequest,
  leadSortType,
  LeadStatus, LeadTakenMessage,
  LeadType, DeadlineStatusOption
} from '../../../../models/leads/leads.model';
import {ContactType} from '../../../../models/contacts/contacts.model';
import {ResourceCountry} from '../../../../models/resources/resources.model';
import {HideLoader, ShowLoader} from '../../../store/loader/loader.actions';
import {combineLatest, firstValueFrom, Observable} from 'rxjs';
import {SetCheckedLeads} from '../../../store/checked-leads/checked-leads.actions';
import {ActionHelper} from '../../../../helper/action.helper';
import {ACTION_DATA_SERVICE, ActionDataService} from '../../../../services/action/data.service';
import {MatDialog} from '@angular/material/dialog';
import {SetActions} from '../../../store/lead-action/lead-action.actions';
import {AddressHelper} from '../../../../helper/address.helper';
import {SetCompactView} from '../../../store/compact-view/compact-view.actions';
import {CompactViewState} from '../../../store/compact-view/compact-view.state';
import {UserProfileShareService} from '../../../../services/shared-service/user-profile-share.service';
import {CheckedLeadsState} from '../../../store/checked-leads/checked-leads.state';
import {takeUntil} from 'rxjs/operators';
import {
  RequestsOverviewDesktopContainerComponent
} from './requests-overview-desktop-container/requests-overview-desktop-container.component';
import {
  RequestsOverviewTopBarComponent
} from '../requests-overview-top-bar/requests-overview-top-bar.component';
import {LeadsQueryRequestHelper} from '../../../../helper/leads-query-request.helper';

export interface FiltersAndSorting {
  fromDate?: Date;
  toDate?: Date;
  sortType?: leadSortType;
  sortDirection?: 'ASC' | 'DESC';
  search?: string;
  categoryKey?: string;

  leadTypes?: LeadType[];
  contactTypes?: ContactType[];
  leadStatuses?: LeadStatus[];
  countries?: ResourceCountry[];
  deadlineStatus?: DeadlineStatusOption[];
  premiumProjectCustomerTypes?: { Id: number; Text: string }[];


  commissionSearches?: string[];
  contactSearches?: string[];
  responsibleSearches?: string[];

  premiumProjectCustomer?: boolean;
  openLeads?: boolean;
  closedLeads?: boolean;
  onlyMyLeads?: boolean;
  unreadLeads?: boolean;

  pageNumber?: number;

  lastUserId?: number;
}

@Component({
  selector: 'fi-requests-overview-content',
  templateUrl: './requests-overview-content.component.html',
  styleUrls: ['./requests-overview-content.component.scss'],
  standalone: false,
})
export class RequestsOverviewContentComponent implements OnDestroy {

  @Select(CompactViewState.isComapct) compactView$: Observable<boolean>;

  @Select(CheckedLeadsState.getMarkCheckedLeadAsReadUnreadId) leadReadUnreadId$: Observable<number>;

  @ViewChild(RequestsOverviewDesktopContainerComponent) desktopContainerComponent: RequestsOverviewDesktopContainerComponent;

  @ViewChild(RequestsOverviewTopBarComponent) topBarComponent: RequestsOverviewTopBarComponent;

  filters: FiltersAndSorting = {};
  readonly PER_PAGE_LEADS_COUNT = 100;

  readonly expandedLeads = new WeakSet<LeadsData>();

  pagesCount = 0;

  leadsCount = 0;
  visibleLeadsCount = 0;
  activeCellHeight = '';

  userId: number;

  contactLeads: LeadsData[];
  desktopAddresses = new WeakMap<LeadsData, { [key: string]: string | boolean | string[] }>();
  leadTakenMessages = new WeakMap<LeadsData, LeadTakenMessage>();
  checkedLeads = new Set<number>();

  rejectText = 'Anfrage ablehnen';
  confirmText = 'Anfrage übernehmen';

  checkedAll = false;

  filterType: string;
  searchFilterOpen = false;

  isMobile: boolean;
  isFinstralUser: boolean;
  showFilterSelectBox: boolean;
  showFiltersOnMobile = false;
  expandedLeadLinkId = -1;
  multiSelectDataLoaded: boolean = false;
  multiselectNameKey: string = '';
  multiselectSelectedOptions: any[] = [];
  multiselectOptions: any[] = [];

  private searchTimeout = null;
  private sortFields: string[] = [];


  private countries: ResourceCountry[] | undefined;
  private contactTypes: ContactType[] | undefined;
  private leadStatuses: LeadStatus[] | undefined;
  private leadTypes: LeadType[] | undefined;
  private premiumProjectCustomerTypes: { Id: number; Text: string }[] | undefined;
  private deadlineStatus: DeadlineStatusOption[];

  private shouldScrollToTopAfterLoad = false;
  private subscriptionEnd$ = new EventEmitter<void>();


  constructor(
    @Inject(CONTACTS_DATA_SERVICE) private contactsDataService: ContactsDataService,
    @Inject(LEADS_DATA_SERVICE) private leadDataService: LeadsDataService,
    @Inject(ACTION_DATA_SERVICE) public actionDataService: ActionDataService,
    private dialog: MatDialog,
    private router: Router,
    private userTokenService: UserTokenService,
    private store: Store,
    private sortService: SortService,
    private readonly sharedProfileService: UserProfileShareService
  ) {
    this.store.dispatch(new SetCompactView(sessionStorage.getItem('compactView') === 'true'));
    this.userId = +this.userTokenService.getId();

    this.actionDataService.getSnippets()
      .pipe(takeUntil(this.subscriptionEnd$))
      .subscribe(data => {
        this.store.dispatch(new SetActions(data.data));
        this.rejectText = data.data?.find(item => item.Name === 'LEAD_REJECT')?.Text ?? this.rejectText;
        this.confirmText = data.data?.find(item => item.Name === 'LEAD_FORWARD_CONFIRM')?.Text ?? this.confirmText;
      });

    this.sharedProfileService.getIsDealerPartner()
      .pipe(takeUntil(this.subscriptionEnd$))
      .subscribe(isDealerPartner => {
        this.isFinstralUser = !isDealerPartner;
      });

    this.loadFilters();

    const search = this.router.parseUrl(this.router.url).queryParamMap.get('q');
    const categoryKey = this.router.parseUrl(this.router.url).queryParamMap.get('n');

    if (search) {
      this.filters.search = search;
    }

    if (categoryKey) {
      this.filters.categoryKey = categoryKey;
    }

    this.isMobile = window.innerWidth <= 880

    this.loadLeads(false);

    this.listenToLeadRead();
  }

  ngOnDestroy(): void {
    this.subscriptionEnd$?.emit();
    this.subscriptionEnd$?.complete?.();
  }

  getLeadTakenMessage = (lead: LeadsData): LeadTakenMessage | null => {
    if (!this.leadTakenMessages.has(lead)) {
      if (lead?.Messages?.length) {
        const newestMessage = lead.Messages
          .reduce((acc, cur) => acc.Date < cur.Date ? cur : acc);

        if (!newestMessage.Read) {
          this.leadTakenMessages.set(lead, newestMessage);
          return newestMessage;
        }
      }

      this.leadTakenMessages.set(lead, null);
    }

    return this.leadTakenMessages.get(lead);
  };

  @HostListener('document:click', ['$event'])
  documentClick(event): void {
    if (this.searchFilterOpen && event.target instanceof HTMLElement && !event.target.closest('.search-container')) {
      this.closeSearchFilter();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth <= 880;
  }

  showLoaderIfNecessary(): void {
    if (!document.activeElement?.closest('.search-input')) {
      this.store.dispatch(new ShowLoader());
    }
  }

  loadLeads(resetPage = true): void {
    if (this.searchTimeout !== null) {
      clearTimeout(this.searchTimeout);
    }

    this.filters = {...this.filters};

  this.searchTimeout = setTimeout(() => {
      if (resetPage) {
        this.shouldScrollToTopAfterLoad = true;
        this.filters.pageNumber = 0;
      }

      this.showLoaderIfNecessary();

      const currentSearchTimeout = this.searchTimeout;

      const isDsl =
        !!this.filters.categoryKey && this.filters.categoryKey !== 'Filters' && !!this.filters.search
        || !!this.filters.leadStatuses?.length
        || !!this.filters.countries?.length
        || !!this.filters.leadTypes?.length
        || !!this.filters.premiumProjectCustomerTypes?.length
        || !!this.filters.contactTypes?.length
        || !!this.filters.responsibleSearches?.length
        || !!this.filters.commissionSearches?.length
        || !!this.filters.contactSearches?.length;

      if (isDsl && !this.filters.categoryKey) {
        this.filters.categoryKey = 'Filters';
      }

      if (!isDsl && this.filters.categoryKey === 'Filters') {
        delete this.filters.categoryKey;
      }

      this.storeFilters();

      SortService.selectedLeadTypes = this.filters.leadTypes?.map(item => item.Name) ?? [];
      SortService.selectedLeadStatuses = this.filters.leadStatuses?.map(item => item.Text) ?? [];
      SortService.selectedLeadPremiumProjectCustomerTypes = this.filters.premiumProjectCustomerTypes?.map(item => item.Text) ?? [];
      SortService.selectedCountries = this.filters.countries?.map(item => item.Name) ?? [];
      SortService.selectedContactTypes = this.filters.contactTypes?.map(item => item.Name) ?? [];

      this.sortService.doSearch(this.filters.search, this.filters.categoryKey);

      if (!this.filters.closedLeads && !this.filters.openLeads) {
        this.scrollToTopIfNecessary();
        this.leadsCount = 0;
        this.contactLeads = [];
        this.pagesCount = 0;
        this.visibleLeadsCount = this.getShownLeadsCount();
        setTimeout(() => {
          this.store.dispatch(new HideLoader());
          this.closeFilterMultiSelect();
        }, 500)
      } else {
        this.getLeads(currentSearchTimeout);
      }

    }, 200);
  }

  listenToLeadRead(): void {
    this.leadReadUnreadId$.pipe(takeUntil(this.subscriptionEnd$)).subscribe((leadId) => {
      if (leadId !== null && leadId !== undefined) {
        const lead = this.contactLeads.find((lead) => lead.Lead.Id === leadId);
        if (lead) {
          lead.Lead.Read = !lead.Lead.Read;
        }
      }
    });
  }

  private addDuplicates(duplicates: LeadDataObject[]): void {
    this.contactLeads.push(...duplicates.map(item => ({Lead: item, isDuplicate: true})));
  }

  private closeFilterMultiSelect(): void {
    this.showFilterSelectBox = false;
  }

  private shouldHideDuplicates(): boolean {
    return !!(
      this.filters.search?.trim()
      || this.filters.fromDate
      || this.filters.toDate
      || this.filters.deadlineStatus?.length
      || this.filters.unreadLeads
      || this.filters.leadTypes?.length
      || this.filters.contactTypes?.length
      || this.filters.leadStatuses?.length
      || this.filters.countries?.length
      || this.filters.premiumProjectCustomerTypes?.length
      || this.filters.commissionSearches?.some(item => item?.trim())
      || this.filters.contactSearches?.some(item => item?.trim())
      || this.filters.responsibleSearches?.some(item => item?.trim())
    );
  }

  private sortLeads(): void {
    if (!this.contactLeads?.length) return;

    // compute flag
    this.contactLeads.forEach((item) => {
      item.requiresConfirmation =
        !!item.Lead?.RequiresConfirmation && +item.Lead?.EditorId === +this.userId;
    });

    // partition into groups
    const toAccept: LeadsData[] = [];
    const duplicates: LeadsData[] = [];
    const regular: LeadsData[] = [];

    this.contactLeads.forEach((item) => {
      if (item.requiresConfirmation) {
        toAccept.push(item);
        return;
      }

      if (item.isDuplicate) {
        duplicates.push(item);
        return;
      }

      regular.push(item);
    });

    // final structure (regular keeps selected sorting from backend)
    this.contactLeads = [...toAccept, ...duplicates, ...regular];
  }

  private getLeads(currentTimeout: number): void {
    const handleResult = (leads?: LeadsData[], count?: number, duplicates?: LeadDataObject[]) => {
      if (this.searchTimeout !== currentTimeout) {
        return;
      }

      this.store.dispatch(new HideLoader());

      if (leads && count !== undefined && count !== null) {
        this.scrollToTopIfNecessary();
        this.leadsCount = count;
        this.contactLeads = leads;
        this.pagesCount = Math.ceil(this.leadsCount / this.PER_PAGE_LEADS_COUNT);
        this.visibleLeadsCount = this.getShownLeadsCount();
        if (duplicates?.length && !this.shouldHideDuplicates()) {
          this.addDuplicates(duplicates);
        }
        this.closeFilterMultiSelect();
        this.sortLeads();
      }
    };

    const queryRequest = this.getQueryRequest();

    if (
      (this.filters.onlyMyLeads || this.filters.openLeads) ||
      (!this.filters.onlyMyLeads && (this.filters.openLeads && this.filters.closedLeads))
    ) {
      combineLatest([
        this.leadDataService.queryLeads(queryRequest),
        this.leadDataService.queryLeadsCount(queryRequest),
        this.leadDataService.getDuplicateLeads(),
      ])
        .pipe(takeUntil(this.subscriptionEnd$))
        .subscribe(([items, count, duplicates]) => {
          handleResult(items.data, count.data, duplicates.data);
        });
    } else {
      combineLatest([
        this.leadDataService.queryLeads(queryRequest),
        this.leadDataService.queryLeadsCount(queryRequest),
      ])
        .pipe(takeUntil(this.subscriptionEnd$))
        .subscribe(([items, count]) => {
          handleResult(items.data, count.data);
        });
    }
  }

  private scrollToTopIfNecessary(): void {
    setTimeout(() => {
      if (this.shouldScrollToTopAfterLoad) {
        window.scrollTo({top: 0, behavior: 'smooth'});
        this.shouldScrollToTopAfterLoad = false;
      }
    }, 200)
  }

  private getQueryRequest(): LeadsQueryRequest {
    return LeadsQueryRequestHelper.build(
      this.filters,
      this.userId,
      this.PER_PAGE_LEADS_COUNT
    );
  }

  private storeFilters(): void {
    localStorage.setItem('filters', JSON.stringify(this.filters));
  }

  private loadFilters(): void {
    try {
      this.filters = JSON.parse(localStorage.getItem('filters') ?? '{}');

      if (this.filters.fromDate) {
        this.filters.fromDate = new Date(this.filters.fromDate);
      }

      if (this.filters.toDate) {
        this.filters.toDate = new Date(this.filters.toDate);
      }
    } catch (e) {
      this.filters = {};
    }

    if (this.filters?.lastUserId !== this.userId) {
      this.filters = null;
    }

    this.filters = this.filters ?? {lastUserId: this.userId};

    if (this.filters.openLeads === undefined) {
      this.filters.openLeads = true;
    }

    if (this.filters.onlyMyLeads === undefined) {
      this.filters.onlyMyLeads = true;
    }

    if (this.filters.pageNumber === undefined) {
      this.filters.pageNumber = 0;
    }

    if (this.filters.sortType === undefined) {
      this.filters.sortType = 'DATE';
      this.filters.sortDirection = 'ASC';
    }

    this.updateSortFields();
  }

  private async getMultiSelectData() {
    switch (this.filterType) {
      case 'TYPE':
        this.multiselectNameKey = 'Description';
        this.multiselectSelectedOptions = this.filters.leadTypes;
        this.multiselectOptions = await this.getLeadTypes();
        break;
      case 'PREMIUM_PROJECT_CUSTOMER':
        this.multiselectNameKey = 'Text';
        this.multiselectSelectedOptions = this.filters.premiumProjectCustomerTypes;
        this.multiselectOptions = await this.getPremiumProjectCustomerTypes();
        break;
      case 'STATUS':
        this.multiselectNameKey = 'Text';
        this.multiselectSelectedOptions = this.filters.leadStatuses;
        this.multiselectOptions = await this.getLeadStatus();
        break;
      case 'CONTACT_MULTISELECT':
        this.multiselectNameKey = 'Description';
        this.multiselectSelectedOptions = this.filters.contactTypes;
        this.multiselectOptions = await this.getContactTypes();
        break;
      case 'LAND':
        this.multiselectNameKey = 'Name';
        this.multiselectSelectedOptions = this.filters.countries;
        this.multiselectOptions = await this.getCountries();
        break;
      case 'DEADLINE_STATUS':
        this.multiselectNameKey = 'Text';
        this.multiselectSelectedOptions = this.filters.deadlineStatus;
        this.multiselectOptions = await this.getDeadlineStatuses();
        break;
      default:
        this.resetMultiSelectData();
    }
  }

  private resetMultiSelectData() {
    this.multiselectNameKey = '';
    this.multiselectSelectedOptions = [];
    this.multiselectOptions = [];
  }

  private async getContactTypes(): Promise<ContactType[]> {
    if (this.contactTypes !== undefined) {
      this.multiSelectDataLoaded = true;
      return this.contactTypes;
    } else {
      this.multiSelectDataLoaded = false;
      const response = await firstValueFrom(this.contactsDataService.getContactTypes());
      const {success, data} = response;
      this.contactTypes = success ? data : [];
      this.multiSelectDataLoaded = true;
      return this.contactTypes;
    }
  }

  private async getLeadStatus(): Promise<LeadStatus[]> {
    if (this.leadStatuses !== undefined) {
      this.multiSelectDataLoaded = true;
      return this.leadStatuses;
    }
    this.multiSelectDataLoaded = false;
    const response = await firstValueFrom(this.leadDataService.getLeadStatus());
    const {success, data} = response;
    this.leadStatuses = success ? data : [];
    this.multiSelectDataLoaded = true;
    return this.leadStatuses;
  }

  private async getLeadTypes(): Promise<LeadType[]> {
    if (this.leadTypes !== undefined) {
      this.multiSelectDataLoaded = true;
      return this.leadTypes;
    }
    this.multiSelectDataLoaded = false;
    const response = await firstValueFrom(this.leadDataService.getLeadTypes());
    const {success, data} = response;
    this.leadTypes = success ? data : [];
    this.multiSelectDataLoaded = true;
    return this.leadTypes;
  }

  private async getCountries(): Promise<ResourceCountry[]> {
    if (this.countries !== undefined) {
      this.multiSelectDataLoaded = true;
      return this.countries;
    }
    this.multiSelectDataLoaded = false;
    const countryData = await AddressHelper.getCountries();
    this.countries = countryData.filter(x => x.IsoCode);

    this.multiSelectDataLoaded = true;
    return this.countries ?? [];
  }

  private async getPremiumProjectCustomerTypes(): Promise<{ Id: number, Text: string }[]> {
    if (this.premiumProjectCustomerTypes !== undefined) {
      this.multiSelectDataLoaded = true;
      return this.premiumProjectCustomerTypes;
    }
    this.multiSelectDataLoaded = false;
    // const response = await firstValueFrom(this.leadDataService.getLeadTypes());
    // const { success, data } = response;
    // this.premiumProjectCustomerTypes = success ? data : [];
    this.premiumProjectCustomerTypes = [{Id: 1, Text: 'REQUEST_OVERVIEW_FILTER_PREMIUM_PARTNER'}];
    this.multiSelectDataLoaded = true;
    return this.premiumProjectCustomerTypes;
  }

  private async getDeadlineStatuses(): Promise<DeadlineStatusOption[]> {
    if (this.deadlineStatus !== undefined) {
      this.multiSelectDataLoaded = true;
      return this.deadlineStatus;
    }
    this.multiSelectDataLoaded = false;
    this.deadlineStatus = [
      {Id: 'green', Text: 'REQUEST_OVERVIEW_FILTER_HIDE_GREEN_STATUS'},
      {Id: 'orange', Text: 'REQUEST_OVERVIEW_FILTER_HIDE_ORANGE_STATUS'},
      {Id: 'red', Text: 'REQUEST_OVERVIEW_FILTER_HIDE_RED_STATUS'},
    ];
    this.multiSelectDataLoaded = true;
    return this.deadlineStatus;
  }

  openLeadDetails(id: number, newTab = false): void {
    const openDetails = () => {
      if (newTab) {
        const url = this.router.serializeUrl(
          this.router.createUrlTree(['/details/' + id])
        );

        window.open(url, '_blank');
      } else {
        this.router.navigate(['/details/' + id]);
      }
    }

    const lead = this.contactLeads.find(x => +x.Lead?.Id === +id);

    if (!lead?.Lead?.Read) {
      this.markAsRead(id);
      openDetails();
    } else {
      openDetails();
    }
  }

  markAsRead(id: number) {
    const lead = this.contactLeads.find(x => +x.Lead?.Id === +id);
    this.leadDataService.markAsRead(id).subscribe(({success}) => {
      if (success) {
        lead.Lead.Read = true;
        this.closeOverlay();
      }
    });
  }

  markAsUnread(id: number): void {
    const lead = this.contactLeads.find(x => +x.Lead?.Id === +id);
    this.leadDataService.markAsUnread(id).subscribe(({success}) => {
      if (success) {
        lead.Lead.Read = false;
        this.closeOverlay();
      }
    });
  }

  addFilter(filterType: string, value: string): void {
    if (value?.trim()) {
      switch (filterType) {
        case 'COMMISSION':
          this.filters.commissionSearches = this.filters.commissionSearches ?? [];
          this.filters.commissionSearches.push(value);
          this.loadLeads();
          break;
        case 'CONTACT_SEARCH':
          this.filters.contactSearches = this.filters.contactSearches ?? [];
          this.filters.contactSearches.push(value);
          this.loadLeads();
          break;
        case 'RESPONSIBLE':
          this.filters.responsibleSearches = this.filters.responsibleSearches ?? [];
          this.filters.responsibleSearches.push(value);
          this.loadLeads();
          break;
      }

      this.closeSearchFilter();
    }
  }

  toggleExpandedLead(lead: LeadsData): void {
    if (this.expandedLeads.has(lead)) {
      this.expandedLeads.delete(lead);
    } else {
      this.expandedLeads.add(lead);
    }
  }

  onMultiselectChanged(value: string[]): void {
    setTimeout(() => {
      document.body.click();
    }, 100)

    switch (this.filterType) {
      case 'TYPE':
        this.filters.leadTypes = this.leadTypes.filter(type => value.find(val => +val === type.Id));
        this.loadLeads();
        break;
      case 'PREMIUM_PROJECT_CUSTOMER':
        this.filters.premiumProjectCustomerTypes = this.premiumProjectCustomerTypes.filter(type => value.find(val => +val === type.Id));
        this.loadLeads();
        break;
      case 'STATUS':
        this.filters.leadStatuses = this.leadStatuses.filter(status => value.find(val => +val === status.Id));
        this.loadLeads();
        break;
      case 'CONTACT_MULTISELECT':
        this.filters.contactTypes = this.contactTypes.filter(type => value.find(val => +val === type.Id));
        this.loadLeads();
        break;
      case 'LAND':
        this.filters.countries = this.countries.filter(country => value.find(val => val === `${country.Id}`));
        this.loadLeads();
        break;
      case 'DEADLINE_STATUS':
        this.filters.deadlineStatus = this.deadlineStatus.filter(status => value.find(val => val === status.Id));
        this.loadLeads();
        break;
    }
  }

  closeSearchFilter(): void {
    this.searchFilterOpen = false;

    setTimeout(() => {
      if (['COMMISSION', 'CONTACT_SEARCH', 'RESPONSIBLE'].includes(this.filterType)) {
        this.filterType = 'NONE';
      }
    }, 150);
  }

  openSearchFilter(type: string): void {
    if (this.filterType === type) {
      return;
    }

    this.searchFilterOpen = false;
    this.filterType = type;

    setTimeout(() => {
      this.searchFilterOpen = true;

      setTimeout(() => {
        document.getElementById(this.filterType)?.focus();
      }, 50)
    }, 50)
  }

  openMultiselectFilter(type: string): void {
    if (document.querySelector(`#${type}.multiselect-container.opened`)) {
      return;
    }

    setTimeout(() => {
      this.searchFilterOpen = false;
      this.showFilterSelectBox = true;
      this.filterType = type;
      this.resetMultiSelectData();
      setTimeout(() => {
        document.getElementById(this.filterType)?.click();
        this.getMultiSelectData();
      }, 50);
    }, 10);
  }

  sortByColumn(sortColumn: leadSortType): void {
    if (this.filters.sortType === sortColumn) {
      this.filters.sortDirection = this.filters.sortDirection === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.filters.sortDirection = sortColumn === 'DATE2' ? 'DESC' : 'ASC';
      this.filters.sortType = sortColumn;
    }

    this.updateSortFields();
    this.loadLeads(false);
  }

  onMobileSortChanged(sort: { sortType: leadSortType; sortDirection: 'ASC' | 'DESC' }): void {
    this.filters.sortType = sort.sortType;
    this.filters.sortDirection = sort.sortDirection;
    this.updateSortFields();
    this.loadLeads(false);
  }

  openMobileSortFilter(): void {
    this.topBarComponent?.openMobileFilters('sort');
  }

  onCategoryTypeChanged(type: string): void {
    this.filters.categoryKey = type;

    if (this.filters.search) {
      this.loadLeads();
    }
  }

  onQueryChanged(query: string): void {
    this.filters.search = query;
    this.loadLeads();
  }

  onRangeSelect(range: Date[]): void {
    if (
      this.filters.fromDate?.valueOf() === range[0]?.valueOf() &&
      this.filters.toDate?.valueOf() === range[1]?.valueOf()
    ) {
      return;
    }

    this.filters.fromDate = range[0];
    this.filters.toDate = range[1];
    this.loadLeads();
  }

  private updateSortFields(): void {
    switch (this.filters.sortType) {
      case 'LAST_ACTION':
        this.setToSortFields('LastAction');
        break;
      case 'TYPE':
        this.setToSortFields('LeadTypeId');
        break;
      case 'STATUS':
        this.setToSortFields('StatusId');
        break;
      case 'CONTACT':
        this.setToSortFields('Contact.FirstName', ['Contact.LastName']);
        break;
      case 'RESPONSIBLE':
        this.setToSortFields('EditorName', ['ExternalDesc']);
        break;
      case 'DATE':
        this.setToSortFields('Deadline');
        break;
      case 'LAND':
        this.setToSortFields('ResolvedAddress.CountryCode');
        break;
      case 'COMMISSION':
        this.setToSortFields('Commission', ['ResolvedAddress.Address', 'ResolvedAddress.ZipCode', 'ResolvedAddress.City']);
        break;
      case 'DATE2':
        this.setToSortFields('Number');
        break;
      default:
        this.sortFields = ['-Number'];
    }
  }

  private setToSortFields(type: string, additionalFields: string[] = []): void {
    const asc = this.sortFields.indexOf(type)
    const desc = this.sortFields.indexOf(`-${type}`)

    this.sortFields = [];

    if (asc !== -1 || (!this.sortFields.length && this.filters.sortDirection === 'DESC')) {
      this.sortFields.push(`-${type}`);
    } else if (desc !== -1) {
      this.sortFields.push(type);
    } else {
      this.sortFields.push(type);
    }

    if (this.sortFields[0]?.startsWith('-')) {
      this.sortFields = [this.sortFields[0], ...additionalFields.map(item => `-${item}`)];
    } else {
      this.sortFields = [this.sortFields[0], ...additionalFields];
    }
  }

  removeAllSelectedTabs(): void {
    this.filters.fromDate = undefined;
    this.filters.toDate = undefined;
    this.filters.leadTypes = [];
    this.filters.premiumProjectCustomerTypes = [];
    this.filters.contactTypes = [];
    this.filters.leadStatuses = [];
    this.filters.countries = [];
    this.filters.deadlineStatus = undefined;
    this.filters.commissionSearches = [];
    this.filters.contactSearches = [];
    this.filters.responsibleSearches = [];
    this.filters.premiumProjectCustomer = undefined;
    this.loadLeads();
  }

  clearDateRange(): void {
    this.filters.fromDate = undefined;
    this.filters.toDate = undefined;
    this.loadLeads();
  }

  removeFilter(filters: any[], index: number): void {
    filters.splice(index, 1);

    for (const key in this.filters) {
      const value = this.filters[key];

      if (value instanceof Array) {
        this.filters[key] = [...value];
      }
    }


    this.filters = {...this.filters};

    this.loadLeads();
  }

  rejectAssignment(index: number): void {
    const lead = this.contactLeads[index];

    const actionData = {
      contactName: lead.Lead.Contact.FirstName + ' ' + lead.Lead.Contact.LastName,
      leadId: lead.Lead.Id,
      title: this.rejectText
    }
    ActionHelper.showAction('LEAD_REJECT', this.dialog, actionData)?.afterClosed().subscribe(data => {
      if (data === 'remove') {
        this.contactLeads = this.contactLeads.filter(item => item !== lead);
      }
    });
  }

  confirmAssignment(index: number): void {
    const lead = this.contactLeads[index];

    if (lead.isDuplicate) {
      this.onDuplicateClick(index);
      return;
    }

    this.actionDataService.confirmRequest(lead.Lead.Id).subscribe(({success, data}) => {
      if (success) {
        this.openLeadDetails(lead.Lead.Id);
      }
    });
  }

  openMapNewTab(event: Event, lead: LeadsData): void {
    if (window.innerWidth > 600) {
      return;
    }
    event.stopPropagation();
    const addressUrl = AddressHelper.getMapUrl(lead.Lead.Contact);
    window.open(addressUrl);
  }

  getAddress = (lead: LeadsData, _desktop = true): any => {
    if (!this.desktopAddresses.has(lead)) {
      let addresses = [
        lead.Lead.Address?.Address,
        lead.Lead.Address?.ZipCode,
        lead.Lead.Address?.City
      ].map(x => x?.trim()).filter(x => x);

      if (!addresses.length) {
        addresses = [
          lead.Lead.Contact?.Address,
          lead.Lead.Contact?.ZipCode,
          lead.Lead.Contact?.City
        ].map(x => x?.trim()).filter(x => x);
      }

      if (lead.Lead.Commission?.trim()) {
        addresses.unshift(lead.Lead.Commission);
      }

      if (addresses.length > 1) {
        addresses = [addresses[0], addresses.slice(1).join(', ')];
      }

      const address = addresses.join('\n');

      this.desktopAddresses.set(lead, {
        addresses: addresses.length ? addresses : null,
        isAddress: !!addresses?.length,
        address
      });
    }

    return this.desktopAddresses.get(lead);
  };

  onDuplicateClick(index: number): void {
    const lead = this.contactLeads[index]?.Lead;

    if (!lead) {
      return;
    }

    ActionHelper.showAction('DUPLICATE-CHECK', this.dialog, {leadId: lead.Id})
      ?.afterClosed()
      .subscribe(value => {
        if (value === 'solved') {
          this.openLeadDetails(lead.Id);
        }
      });
  }

  checkLead(lead: LeadsData): void {
    if (this.checkedLeads.has(lead.Lead.Id)) {
      this.checkedLeads.delete(lead.Lead.Id);
    } else {
      this.checkedLeads.add(lead.Lead.Id);
    }
    this.store.dispatch(new SetCheckedLeads([...this.checkedLeads], false));
  }


  checkAllFields(check: boolean): void {
    this.checkedAll = check;

    if (check) {
      this.checkedLeads = new Set(
        this.contactLeads
          .filter(item => !item.isDuplicate)
          .map(item => item.Lead.Id)
      );
    } else {
      this.checkedLeads.clear()
    }

    this.store.dispatch(new SetCheckedLeads([...this.checkedLeads], true));
  }

  activeIndex = -1;
  showActions = false;

  onCompactViewToggle(compactView: boolean): void {
    sessionStorage.setItem('compactView', `${compactView}`);
    this.store.dispatch(new SetCompactView(compactView));
  }

  closeOverlay(reloadLeads = false, event?: MouseEvent): void {
    const lastActiveIndex = this.activeIndex;
    this.activeIndex = -1;
    this.showActions = false;

    if (reloadLeads) {
      this.loadLeads(false);
    }

    if (event) {
      const el = document.elementsFromPoint(event.clientX, event.clientY)[1];
      if (el instanceof HTMLElement && +el.closest('button')?.dataset.actionIndex !== lastActiveIndex) {
        el.click();
      }
    }
  }

  openActionPopup(index: number, event: MouseEvent): void {
    event.stopPropagation();
    this.showActions = false;
    this.activeIndex = index;
    this.activeCellHeight = (event.target as HTMLElement).closest('td').clientHeight + 'px';

  }

  openSuggestedActionPopup(index: number): void {
    if (+this.contactLeads[index].Lead.EditorId !== +this.userId) {
      this.openLeadDetails(this.contactLeads[index].Lead.Id);
      return;
    }

    if (!this.desktopContainerComponent) {
      return;
    }

    this.desktopContainerComponent.openSuggestedActionTablePopup(
      this.contactLeads[index].Lead,
      this.contactLeads[index].SuggestedAction
    );
  }

  nextPage(): void {
    this.filters.pageNumber = this.filters.pageNumber ?? 0;
    this.filters.pageNumber++;
    this.shouldScrollToTopAfterLoad = true;
    this.loadLeads(false);
  }

  prevPage(): void {
    this.filters.pageNumber = this.filters.pageNumber ?? 0;
    this.filters.pageNumber--;
    this.shouldScrollToTopAfterLoad = true;
    this.loadLeads(false);
  }

  private getShownLeadsCount(): number {
    return Math.min((this.filters.pageNumber ?? 0) * this.PER_PAGE_LEADS_COUNT + 100, this.leadsCount);
  }
}
